import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  MsalService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from "@azure/msal-angular";
import {
  AuthenticationResult,
  InteractionType,
  PopupRequest,
  RedirectRequest,
} from "@azure/msal-browser";
import { environment } from "@env/environment";
import { SharedService } from "@shared/services/shared.service";
import { Observable, Subject } from "rxjs";

@Injectable({
  providedIn: "root",
})

export class AuthService {
  public loading: boolean = false;
  public static b2cAuthType: string = "B2C";
  public static aadAuthType: string = "AAD";
  userRoles: string[] = [];
  public rolesUpdated: Subject<string[]> = new Subject<string[]>();
  private readonly _destroying$ = new Subject<void>();
  msalBroadcastService: any;
  public rolesChanged$ = this.rolesUpdated.asObservable();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private http: HttpClient,
    private sharedService: SharedService,
    private router: Router) {
  }

  isAuthenticated(): boolean {
    return this.authService.instance.getAllAccounts().length > 0;
  }

  public getUserRoles(): Observable<string[]> {
    return this.http.get<string[]>(environment.privateApi + "User/roles");
  }

  public getUserNameClaim(): Observable<string> {
    return this.http.get<string>(environment.privateApi + "User/user/claim/name", { responseType: 'text' as 'json' });
  }

  public loadUserInformation(): void {
    this.sharedService.loading = true;
    this.getUserRoles().subscribe({
      next: response => {
        this.userRoles = response;
        this.rolesUpdated.next(response);
        this.sharedService.loading = false;
      },
      error: e => {
        console.error("auth error: " + e.statusText);
        this.sharedService.loading = false;
      }
    });
  }

  public isAuthorized(roles?: string[]): boolean {
    if (roles != null && roles.length > 0) {
      return (this.isAuthenticated() && this.userHasAtLeastOneRole(roles));
    }
    return this.isAuthenticated();
  }

  public userHasAtLeastOneRole(roles: string[]): boolean {
    if (roles == null || roles.length == 0 || this.userRoles.length == 0) {
      return false;
    }

    for (let role of roles) {
      if (this.userRoles.findIndex((r) => r == role) >= 0) {
        return true;
      }
    }
    return false;
  }

  public get username(): string {
    let account = this.authService.instance.getAllAccounts()[0];

    if (account) {
      const givenName = account.idTokenClaims["given_name"] as string;
      const familyName = account.idTokenClaims["family_name"] as string;

      const formattedGivenName = givenName ? this.formatName(givenName) : '';
      const formattedFamilyName = familyName ? this.formatName(familyName) : '';
      let name: string = `${formattedGivenName} ${formattedFamilyName}`.trim();
      if (name.length > 0) {
        return name;
      }
    }
    return this.email;
  }

  public get email(): string {
    let account = this.authService.instance.getAllAccounts()[0];
    if (account) {
      const emails = account.idTokenClaims["emails"];
      if (!emails)
        return null;

      if (emails.length > 0) {
        return emails[0];
      }
    }
    return null;
  }

  public get isAdmin(): boolean {
    return this.userRoles.includes('Admin');
  }

  public get IsDeveloper(): boolean {
    return this.userRoles.includes('Developer');
  }

  public get isProvider(): boolean {
    return this.userRoles.includes('Provider');
  }

  public get isSurveyor(): boolean {
    return this.userRoles.includes('Surveyor');
  }

  public get isLicenseSpecialist(): boolean {
    return this.userRoles.includes('LicenseSpecialist');
  }

  public get isOPA3(): boolean {
    return this.userRoles.includes('OPA3');
  }

  public get isProgramManager(): boolean {
    return this.userRoles.includes('ProgramManager');
  }

  public get isSectionManager(): boolean {
    return this.userRoles.includes('SectionManager');
  }

  public get isSectionAdministrator(): boolean {
    return this.userRoles.includes('SectionAdministrator');
  }

  public isB2C(): boolean {
    return AuthService.b2cAuthType == environment.auth.defaultAuth;
  }

  public isAAD(): boolean {
    return AuthService.aadAuthType == environment.auth.defaultAuth;
  }

  formatName(name: string): string {
    return name[0].toUpperCase() + name.slice(1).toLowerCase();
  }

  login(userFlowRequest?: RedirectRequest | PopupRequest) {
    this.loading = true;
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.authService
          .loginPopup({
            ...this.msalGuardConfig.authRequest,
            ...userFlowRequest,
          } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
            this.loadUserInformation();
            this.loading = false;
          });
      } else {
        this.authService
          .loginPopup(userFlowRequest)
          .subscribe((response: AuthenticationResult) => {
            console.log(JSON.stringify(response));
            this.authService.instance.setActiveAccount(response.account);
            this.loadUserInformation();
            this.loading = false;
          });
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.authService.loginRedirect({
          ...this.msalGuardConfig.authRequest,
          ...userFlowRequest,
        } as RedirectRequest);
      } else {
        this.authService.loginRedirect(userFlowRequest);
      }
    }
  }

  logout() {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: "/",
      });
    } else {
      this.authService.logoutRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
